import React from 'react';
import { Calendar, MapPin, TrendingUp, FileText, Scale, Globe, ArrowRight, ExternalLink, Zap, Newspaper } from 'lucide-react';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';

const TrendingNewsCard = ({ newsItem, variant = 'default' }) => {
  const isStrip = variant === 'strip';

  const getTypeIcon = (type) => {
    const cls = isStrip ? 'h-4 w-4' : 'h-5 w-5';
    switch (type) {
      case 'legislation':
        return <Scale className={cls} />;
      case 'trade_deal':
        return <FileText className={cls} />;
      case 'tariff':
        return <TrendingUp className={cls} />;
      case 'trade_event':
        return <Zap className={cls} />;
      case 'raw_news':
      case 'external_news':
        return <Newspaper className={cls} />;
      default:
        return <Globe className={cls} />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'legislation':
        return 'bg-purple-100 text-purple-800 border-purple-300 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-700';
      case 'trade_deal':
        return 'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700';
      case 'tariff':
        return 'bg-orange-100 text-orange-800 border-orange-300 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-700';
      case 'trade_event':
        return 'bg-teal-100 text-teal-800 border-teal-300 dark:bg-teal-900/30 dark:text-teal-300 dark:border-teal-700';
      case 'raw_news':
      case 'external_news':
        return 'bg-indigo-100 text-indigo-800 border-indigo-300 dark:bg-indigo-900/30 dark:text-indigo-300 dark:border-indigo-700';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600';
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'legislation':
        return 'Legislation';
      case 'trade_deal':
        return 'Trade Deal';
      case 'tariff':
        return 'Tariff Update';
      case 'trade_event':
        return 'AI Intelligence';
      case 'raw_news':
        return 'Trade News';
      case 'external_news':
        return 'External News';
      default:
        return 'News';
    }
  };

  const getImpactBadge = (item) => {
    if (item.type === 'legislation' && item.impact_type) {
      const color = item.impact_type === 'Liberalizing'
        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
        : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
      return (
        <span className={`rounded font-medium ${isStrip ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-1 text-xs'} ${color}`}>
          {item.impact_type}
        </span>
      );
    }
    if (item.type === 'trade_deal' && item.status) {
      const color = item.status === 'Active'
        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
        : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
      return (
        <span className={`rounded font-medium ${isStrip ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-1 text-xs'} ${color}`}>
          {item.status}
        </span>
      );
    }
    if (item.type === 'trade_event' && item.impact_score) {
      const score = parseInt(item.impact_score);
      const color = score >= 60 ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
        : score >= 40 ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
        : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      return (
        <span className={`rounded font-medium ${isStrip ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-1 text-xs'} ${color}`}>
          Impact: {score}
        </span>
      );
    }
    return null;
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    try {
      return format(new Date(date), 'MMM dd, yyyy');
    } catch {
      return 'N/A';
    }
  };

  const hasExternalUrl = newsItem.url && (
    newsItem.url.startsWith('http://') || newsItem.url.startsWith('https://')
  );

  const getInternalPath = (item) => {
    switch (item.type) {
      case 'legislation':
        return '/legislation';
      case 'trade_deal':
        return '/trade-deals';
      case 'tariff':
        return '/tariffs';
      default:
        return null;
    }
  };

  const internalPath = getInternalPath(newsItem);

  const cardContent = isStrip ? (
    <>
      <div className="mb-2 flex items-start justify-between gap-2">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <div className={`shrink-0 rounded-lg p-1.5 ${getTypeColor(newsItem.type)}`}>
            {getTypeIcon(newsItem.type)}
          </div>
          <span className={`truncate rounded-full border px-2 py-0.5 text-[10px] font-medium ${getTypeColor(newsItem.type)}`}>
            {getTypeLabel(newsItem.type)}
          </span>
        </div>
        {getImpactBadge(newsItem)}
      </div>

      <h3 className="mb-2 line-clamp-2 font-headline text-sm font-semibold leading-snug text-slate-900 dark:text-on-surface">
        {newsItem.title}
      </h3>

      {newsItem.source && (
        <div className="mb-2">
          <span className="inline-block max-w-full truncate rounded-stitch bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-600 dark:bg-surface-container-high dark:text-on-surface-variant">
            {newsItem.source}
          </span>
        </div>
      )}

      <div className="mt-auto flex items-center justify-between gap-2 rounded-stitch-lg bg-slate-50 px-2 py-2 dark:bg-surface-container-lowest">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-slate-600 dark:text-on-surface-variant">
          {newsItem.country_name && (
            <span className="flex max-w-[40%] items-center truncate">
              <MapPin className="mr-0.5 h-3 w-3 shrink-0" />
              <span className="truncate">{newsItem.country_name}</span>
            </span>
          )}
          <span className="flex shrink-0 items-center">
            <Calendar className="mr-0.5 h-3 w-3" />
            {formatDate(newsItem.date)}
          </span>
        </div>
        {hasExternalUrl ? (
          <ExternalLink className="h-3.5 w-3.5 shrink-0 text-primary-600 dark:text-primary" />
        ) : (
          <ArrowRight className="h-3.5 w-3.5 shrink-0 text-primary-600 dark:text-primary" />
        )}
      </div>
    </>
  ) : (
    <>
      <div className="mb-3 flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className={`rounded-lg p-2 ${getTypeColor(newsItem.type)}`}>
            {getTypeIcon(newsItem.type)}
          </div>
          <div>
            <span className={`rounded-full border px-3 py-1 text-xs font-medium ${getTypeColor(newsItem.type)}`}>
              {getTypeLabel(newsItem.type)}
            </span>
          </div>
        </div>
        {getImpactBadge(newsItem)}
      </div>

      <h3 className="mb-2 line-clamp-2 font-headline text-lg font-semibold text-slate-900 dark:text-on-surface">
        {newsItem.title}
      </h3>

      {newsItem.description && (
        <p className="mb-4 line-clamp-2 text-sm text-slate-600 dark:text-on-surface-variant">
          {newsItem.description}
        </p>
      )}

      {newsItem.source && (
        <div className="mb-3">
          <span className="inline-flex items-center rounded-stitch bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600 dark:bg-surface-container-high dark:text-on-surface-variant">
            {newsItem.source}
          </span>
        </div>
      )}

      <div className="mt-4 flex items-center justify-between rounded-stitch-lg bg-slate-50 px-1 py-3 dark:bg-surface-container-lowest">
        <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600 dark:text-on-surface-variant">
          {newsItem.country_name && (
            <div className="flex items-center">
              <MapPin className="mr-1 h-4 w-4" />
              <span>{newsItem.country_name}</span>
            </div>
          )}
          {newsItem.participant_countries && newsItem.participant_countries.length > 0 && (
            <div className="flex items-center">
              <Globe className="mr-1 h-4 w-4" />
              <span>{newsItem.participant_countries.filter(Boolean).join(', ')}</span>
            </div>
          )}
          <div className="flex items-center">
            <Calendar className="mr-1 h-4 w-4" />
            <span>{formatDate(newsItem.date)}</span>
          </div>
        </div>
        {hasExternalUrl ? (
          <ExternalLink className="h-4 w-4 text-primary-600 dark:text-primary" />
        ) : (
          <ArrowRight className="h-4 w-4 text-primary-600 dark:text-primary" />
        )}
      </div>

      {(newsItem.affected_sectors || newsItem.tariff_rate) && (
        <div className="mt-3 flex flex-wrap gap-2 rounded-stitch-lg bg-slate-50 px-2 py-3 dark:bg-surface-container-lowest">
          {newsItem.affected_sectors && newsItem.affected_sectors.length > 0 && (
            newsItem.affected_sectors.slice(0, 2).map((sector, idx) => (
              <span
                key={idx}
                className="rounded-stitch bg-slate-200/80 px-2 py-1 text-xs text-slate-700 dark:bg-secondary-container dark:text-on-secondary-container"
              >
                {sector}
              </span>
            ))
          )}
          {newsItem.tariff_rate && (
            <span className="rounded bg-orange-50 px-2 py-1 text-xs font-medium text-orange-700 dark:bg-orange-900/30 dark:text-orange-300">
              {newsItem.tariff_rate}% Rate
            </span>
          )}
        </div>
      )}
    </>
  );

  const shellBase =
    'block rounded-stitch-xl border border-slate-200/80 bg-white transition-all duration-200 hover:border-primary-500/40 hover:shadow-intel-glow dark:border-outline-variant/15 dark:bg-surface-container dark:hover:border-primary/30';
  const shellPadding = isStrip ? 'p-4 h-full flex flex-col' : 'p-6';

  if (hasExternalUrl) {
    return (
      <a
        href={newsItem.url}
        target="_blank"
        rel="noopener noreferrer"
        className={`${shellBase} ${shellPadding}`}
      >
        {cardContent}
      </a>
    );
  }

  if (internalPath) {
    return (
      <Link to={internalPath} className={`${shellBase} ${shellPadding}`}>
        {cardContent}
      </Link>
    );
  }

  return (
    <div className={`rounded-stitch-xl border border-slate-200/80 bg-white dark:border-outline-variant/15 dark:bg-surface-container ${isStrip ? 'p-4 h-full flex flex-col' : 'p-6'}`}>
      {cardContent}
    </div>
  );
};

export default TrendingNewsCard;
